import { useCallback, useEffect, useRef, useState } from 'react';
import {
  connectCustomer,
  disconnectCustomer,
  getCustomerStatus,
  getCustomers,
} from '../api/etlServiceApi';

const STATUS_POLL_INTERVAL_MS = 3000;  // poll "connecting" customers every 3 s
const LIST_REFRESH_INTERVAL_MS = 30000; // refresh full list every 30 s

/**
 * Manages the list of ETL customers and their VPN/container lifecycle.
 *
 * @param {object} [opts]
 * @param {Function} [opts.onCustomerActive] - called with customerId when a customer becomes active
 */
export function useCustomers({ onCustomerActive } = {}) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectingId, setConnectingId] = useState(null);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const pollingRef = useRef(new Map()); // Map<customerId, intervalId>
  const listTimerRef = useRef(null);

  // ── Load customer list ──────────────────────────────────────────────────────

  const loadCustomers = useCallback(async () => {
    try {
      const data = await getCustomers();
      const list = data.customers ?? [];
      setCustomers(list);
      // If the selected customer is no longer active (idle timeout, or any
      // other server-side disconnect), drop the selection so the UI falls
      // back to the customer picker instead of a stale, dead-end tab.
      setSelectedCustomerId((prevId) => {
        if (!prevId) return prevId;
        const match = list.find((c) => c.customer_id === prevId);
        return match && match.status === 'active' ? prevId : null;
      });
    } catch {
      // backend unreachable — keep stale list
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    listTimerRef.current = setInterval(loadCustomers, LIST_REFRESH_INTERVAL_MS);
    return () => clearInterval(listTimerRef.current);
  }, [loadCustomers]);

  // ── Poll a "connecting" customer until active/error ─────────────────────────

  const stopPolling = useCallback((customerId) => {
    if (customerId) {
      const id = pollingRef.current.get(customerId);
      if (id != null) {
        clearInterval(id);
        pollingRef.current.delete(customerId);
      }
    } else {
      // No id → stop all (used in cleanup effect)
      pollingRef.current.forEach((id) => clearInterval(id));
      pollingRef.current.clear();
    }
  }, []);

  const startPolling = useCallback(
    (customerId) => {
      stopPolling(customerId); // clear only this customer's prior timer
      const intervalId = setInterval(async () => {
        try {
          const customer = await getCustomerStatus(customerId);
          setCustomers((prev) =>
            prev.map((c) => (c.customer_id === customerId ? customer : c)),
          );
          if (customer.status === 'active') {
            setConnectingId(null);
            stopPolling(customerId);
            setSelectedCustomerId(customerId);
            onCustomerActive?.(customerId);
          } else if (customer.status === 'error') {
            setConnectingId(null);
            stopPolling(customerId);
          }
        } catch {
          // transient — keep polling
        }
      }, STATUS_POLL_INTERVAL_MS);
      pollingRef.current.set(customerId, intervalId);
    },
    [stopPolling, onCustomerActive],
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const connect = useCallback(
    async (customerId) => {
      setConnectingId(customerId);
      try {
        const customer = await connectCustomer(customerId);
        setCustomers((prev) =>
          prev.map((c) => (c.customer_id === customerId ? customer : c)),
        );
        if (customer.status === 'active') {
          setConnectingId(null);
          setSelectedCustomerId(customerId);
          onCustomerActive?.(customerId);
        } else {
          startPolling(customerId);
        }
      } catch (err) {
        setConnectingId(null);
        setCustomers((prev) =>
          prev.map((c) =>
            c.customer_id === customerId
              ? { ...c, status: 'error', error: err?.response?.data?.detail ?? 'Connect failed' }
              : c,
          ),
        );
      }
    },
    [startPolling, onCustomerActive],
  );

  const disconnect = useCallback(async (customerId) => {
    setDisconnectingId(customerId);
    try {
      const customer = await disconnectCustomer(customerId);
      setCustomers((prev) =>
        prev.map((c) => (c.customer_id === customerId ? customer : c)),
      );
      if (selectedCustomerId === customerId) {
        setSelectedCustomerId(null);
      }
    } finally {
      setDisconnectingId(null);
    }
  }, [selectedCustomerId]);

  const selectCustomer = useCallback((customerId) => {
    setSelectedCustomerId(customerId);
  }, []);

  // ── Derived ──────────────────────────────────────────────────────────────────

  const selectedCustomer = customers.find((c) => c.customer_id === selectedCustomerId) ?? null;
  const activeCustomers = customers.filter((c) => c.status === 'active');

  return {
    customers,
    selectedCustomerId,
    selectedCustomer,
    activeCustomers,
    loading,
    connectingId,
    disconnectingId,
    connect,
    disconnect,
    selectCustomer,
    reload: loadCustomers,
  };
}
